import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';
import { Plus, Mail, Send, Eye, BarChart3, Loader2 } from 'lucide-react';

const STAGE_OPTIONS = [
  { id: 'all', name: 'All Stages' },
  { id: 'lead_captured', name: 'Lead Captured' },
  { id: 'intake_submitted', name: 'Intake Submitted' },
  { id: 'discovery_call_completed', name: 'Discovery Call Completed' },
  { id: 'design_deck_in_development', name: 'Design Deck In Development' },
  { id: 'design_deck_delivered', name: 'Design Deck Delivered' },
  { id: 'client_reviewing', name: 'Client Reviewing' },
  { id: 'revision_phase', name: 'Revision Phase' },
  { id: 'approved', name: 'Approved' },
  { id: 'production_vendor_alignment', name: 'Production / Vendor Alignment' },
];

export default function Campaigns() {
  const { isAdmin } = useAuth();
  const { get, post, loading } = useApi();
  
  const [campaigns, setCampaigns] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    content: '',
    segment_filters: { stage: 'all' },
  });

  const fetchCampaigns = async () => {
    try {
      const data = await get('/campaigns');
      setCampaigns(data);
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        segment_filters: {
          ...formData.segment_filters,
          stage: formData.segment_filters.stage === 'all' ? '' : formData.segment_filters.stage
        }
      };
      await post('/campaigns', submitData);
      toast.success('Campaign created');
      setCreateDialogOpen(false);
      setFormData({ name: '', subject: '', content: '', segment_filters: { stage: 'all' } });
      fetchCampaigns();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleSendCampaign = async (campaignId) => {
    try {
      const result = await post(`/campaigns/${campaignId}/send`);
      toast.success(result.message);
      fetchCampaigns();
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="campaigns-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-semibold mb-2">Campaigns</h1>
            <p className="text-muted-foreground">
              Create and manage email campaigns for your pipeline
            </p>
          </div>
          
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm" data-testid="create-campaign-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Campaign
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="create-campaign-dialog">
              <DialogHeader>
                <DialogTitle className="font-serif text-xl">New Campaign</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="space-y-2">
                  <Label>Campaign Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="rounded-sm"
                    placeholder="Q1 Outreach"
                    data-testid="campaign-name"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Email Subject *</Label>
                  <Input
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    required
                    className="rounded-sm"
                    placeholder="Premium Hospitality Opportunities"
                    data-testid="campaign-subject"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Target Stage</Label>
                  <Select
                    value={formData.segment_filters.stage}
                    onValueChange={(v) => setFormData({ 
                      ...formData, 
                      segment_filters: { ...formData.segment_filters, stage: v } 
                    })}
                  >
                    <SelectTrigger className="rounded-sm" data-testid="campaign-stage">
                      <SelectValue placeholder="All stages" />
                    </SelectTrigger>
                    <SelectContent>
                      {STAGE_OPTIONS.map(stage => (
                        <SelectItem key={stage.id} value={stage.id}>{stage.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Email Content *</Label>
                  <Textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    required
                    rows={8}
                    className="rounded-sm resize-none"
                    placeholder="Write your email content here..."
                    data-testid="campaign-content"
                  />
                </div>
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)} className="rounded-sm">
                    Cancel
                  </Button>
                  <Button type="submit" className="rounded-sm" disabled={loading} data-testid="campaign-submit-btn">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Campaigns List */}
        <div className="grid grid-cols-1 gap-4">
          {campaigns.length === 0 ? (
            <Card className="rounded-sm border">
              <CardContent className="py-16 text-center">
                <Mail className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">No campaigns yet</p>
              </CardContent>
            </Card>
          ) : (
            campaigns.map((campaign) => (
              <Card key={campaign.id} className="rounded-sm border" data-testid={`campaign-card-${campaign.id}`}>
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-medium">{campaign.name}</h3>
                        <Badge 
                          variant="secondary" 
                          className={`rounded-sm text-xs ${
                            campaign.status === 'sent' ? 'bg-emerald-500 text-white' : 'bg-muted'
                          }`}
                        >
                          {campaign.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-1">
                        Subject: {campaign.subject}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Created {formatDate(campaign.created_at)}
                        {campaign.segment_filters?.stage && (
                          <> • Target: {STAGE_OPTIONS.find(s => s.id === campaign.segment_filters.stage)?.name}</>
                        )}
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-6">
                      {campaign.status === 'sent' && (
                        <div className="flex items-center gap-4 text-sm">
                          <div className="text-center">
                            <p className="font-semibold">{campaign.sent_count}</p>
                            <p className="text-xs text-muted-foreground">Sent</p>
                          </div>
                          <div className="text-center">
                            <p className="font-semibold">{campaign.open_count}</p>
                            <p className="text-xs text-muted-foreground">Opens</p>
                          </div>
                          <div className="text-center">
                            <p className="font-semibold">{campaign.click_count}</p>
                            <p className="text-xs text-muted-foreground">Clicks</p>
                          </div>
                        </div>
                      )}
                      
                      {isAdmin && campaign.status === 'draft' && (
                        <Button 
                          onClick={() => handleSendCampaign(campaign.id)} 
                          className="rounded-sm"
                          disabled={loading}
                          data-testid={`send-campaign-${campaign.id}`}
                        >
                          <Send className="h-4 w-4 mr-2" />
                          Send
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Note about mock emails */}
        <Card className="rounded-sm border bg-muted/30">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground text-center">
              <strong>Note:</strong> Email sending is currently mocked. Connect your email service provider to enable actual delivery.
            </p>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
